import { Select, SelectProps } from '@app/components';
import { extractData } from '@app/helpers/utils';
import { usePagination, useThrottle } from '@app/hooks';
import { ColumnDef, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import React, { forwardRef, useState } from 'react';
import { IPaginatedData, QueryManyBuilder } from '@app/common';
import { baseQueryWithReauth } from '@app/store/api';
import { UseQuery } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import { QueryDefinition } from '@reduxjs/toolkit/dist/query';
import { SimpleSelectHandle } from './SimpleSelect';

export type ResourceSelectParentProps<TData> = Omit<SelectProps<TData>, 'table' | 'control' | 'onSelect' | 'onRemove' | 'render' | 'values'>;

export type ResourceSelectProps<TData> = {
  label?: string;
  nameKey?: string;
  sort?: string[];
  searchKey?: string;
  baseFilters?: Record<string, any>;
  onBuildFilters?: (search: string, baseFilters?: Record<string, any>) => Record<string, any>;
  useQuery: UseQuery<QueryDefinition<QueryManyBuilder<TData>, typeof baseQueryWithReauth, any, IPaginatedData<TData>>>
  columns: ColumnDef<TData, any>[];
} & ResourceSelectParentProps<TData>

export const ResourceSelect = forwardRef<SimpleSelectHandle, ResourceSelectProps<any>>(({
  useQuery, nameKey = 'name',
  sort = [`${nameKey}:ASC`],
  baseFilters = {},
  onBuildFilters,
  searchKey,
  columns,
  ...props
}: ResourceSelectProps<any>, ref: React.Ref<SimpleSelectHandle>) => {
  const [search, setSearch] = useState('');

  const throttledValue = useThrottle(search, 250);

  const { pagination } = usePagination({
    take: 25,
  });

  // Construction des filtres personnalisés
  const buildFilters = () => {
    if (onBuildFilters) {
      return onBuildFilters(throttledValue, baseFilters);
    }

    // Comportement par défaut
    const searchField = searchKey || nameKey;
    if (!throttledValue) {
      return baseFilters;
    }

    return {
      ...baseFilters,
      [searchField]: {
        $contains: throttledValue
      }
    };
  };

  const { data } = useQuery({
    pagination,
    sort,
    filters: buildFilters()
  } as any);

  const table = useReactTable({
    data: extractData(data),
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <Select
      {...props}
      table={table}
      onSearch={setSearch}
      ref={ref}
    />
  );
});
